import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney } from '../utils/currencyUtil';
import {
  addDays, formatPlanDate, formatPlanDateInZone, formatPlanDuration, formatTimeZoneLabel,
} from '../utils/subscriptionDateUtil';
import {
  fetchApplicationReceipt,
  planApprovalDates,
  approveSubscriptionApplication,
  rejectSubscriptionApplication,
} from '../utils/posSubscriptionUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { describeEmailResult, sendApplicationApprovedEmail } from '../utils/emailServiceUtil';
import { POS_APPLICATION_REJECT_REASON_MAX_LENGTH } from '../constants/posConst';
import '../css/Pos.css';
import '../css/PosAdmin.css';
import '../css/PosSubscription.css';

/**
 * One pending application, opened from the Clients screen's side panel: what
 * was asked for, what was paid, the receipt, and the dates approval would set.
 *
 * Approve activates the plan straight away (see
 * approveSubscriptionApplication). Reject asks for a reason, which the client
 * reads on their till's banner.
 *
 * Closes on the × or Close only — never on a backdrop tap, which would lose a
 * half-typed rejection reason.
 *
 * @param {object} props
 * @param {object|null} props.application - null renders nothing
 * @param {object} props.config - for the currency symbol
 * @param {string} props.signedInEmail
 * @param {() => void} props.onClose
 * @param {(result: {type: string, title: string, message: string}) => void} props.onDone -
 *   called after a successful review, with what to tell the administrator
 * @returns {JSX.Element|null}
 */
function PosApplicationReviewModal({ application, config, signedInEmail, onClose, onDone }) {
  const [receipt, setReceipt] = useState('');
  const [receiptLoading, setReceiptLoading] = useState(true);
  const [dates, setDates] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // The receipt and the would-be dates are loaded per application: the panel
  // lists applications without their photos, and the dates depend on the
  // client's current plan at the moment of review.
  useEffect(() => {
    if (!application) return undefined;
    let active = true;
    setReceipt('');
    setReceiptLoading(true);
    setDates(null);
    setRejecting(false);
    setReason('');
    setError('');

    fetchApplicationReceipt(application.clientId, application.id)
      .then((url) => { if (active) setReceipt(url); })
      .catch((loadError) => {
        console.error('Could not load receipt:', loadError);
        if (active) setError('The receipt could not be loaded.');
      })
      .finally(() => { if (active) setReceiptLoading(false); });

    planApprovalDates(application)
      .then((result) => { if (active) setDates(result); })
      .catch((loadError) => console.error('Could not work out approval dates:', loadError));

    return () => { active = false; };
  }, [application]);

  if (!application) return null;

  /** Approves and activates. @returns {Promise<void>} */
  const handleApprove = async () => {
    setBusy(true);
    setError('');
    try {
      const {
        subscription, addedStaff, skippedStaff, reEnabled, clientEmail,
      } = await approveSubscriptionApplication(application, signedInEmail);
      // Awaited so the result can say whether the client was told. The plan
      // is already active either way.
      const emailResult = await sendApplicationApprovedEmail({
        application, subscription, addedStaff, reEnabled, clientEmail,
      }, config);
      const lines = [
        `${application.clientName} is now on ${subscription.planName} from ${formatPlanDate(subscription.startDate)} to ${formatPlanDateInZone(subscription.endDate, dates?.timeZone)}.`,
      ];
      if (reEnabled) lines.push('', 'Their till had been disabled and is now open again.');
      if (addedStaff.length > 0) {
        lines.push('', `Added to their staff list: ${addedStaff.join(', ')}. Each still needs a Firebase Auth account before it can sign in.`);
      }
      if (skippedStaff.length > 0) {
        lines.push('', 'Not added:', ...skippedStaff.map(({ email, reason: why }) => `${email} — ${why}`));
      }
      const emailLine = describeEmailResult(emailResult, clientEmail);
      if (emailLine) lines.push('', emailLine);
      const emailFailed = emailResult.noRecipient || (!emailResult.success && !emailResult.skipped);
      onDone({
        type: skippedStaff.length > 0 || emailFailed ? 'warning' : 'success',
        title: 'Subscription activated',
        message: lines.join('\n'),
      });
    } catch (approveError) {
      console.error('Approving application failed:', approveError);
      setError(approveError.isUserFacing
        ? approveError.message
        : describeWriteError(approveError, { what: 'this approval', path: 'posSubscriptionApplications' }));
      setBusy(false);
    }
  };

  /** Rejects with the typed reason. @returns {Promise<void>} */
  const handleReject = async () => {
    setBusy(true);
    setError('');
    try {
      await rejectSubscriptionApplication(application, reason, signedInEmail);
      onDone({
        type: 'info',
        title: 'Application rejected',
        message: `${application.clientName} will see your reason on their till and can apply again.`,
      });
    } catch (rejectError) {
      console.error('Rejecting application failed:', rejectError);
      setError(rejectError.isUserFacing
        ? rejectError.message
        : describeWriteError(rejectError, { what: 'this rejection', path: 'posSubscriptionApplications' }));
      setBusy(false);
    }
  };

  const extras = application.extraStaffEmails || [];
  // Applications from before free staff accounts were honoured have no split
  // recorded: every account on them was charged.
  const freeStaffCount = Number(application.freeStaffCount) || 0;
  const paidStaffCount = application.paidStaffCount ?? (extras.length - freeStaffCount);
  // A client still inside a paid period renews from the day after it ends, so
  // paying early costs them nothing. Said out loud so the dates make sense.
  const extendsCurrent = Boolean(dates?.current?.endDate)
    && dates.startDate === addDays(dates.current.endDate, 1);

  return createPortal(
    <div className="pos-modal-overlay">
      <div className="pos-modal ps-review" role="dialog" aria-modal="true" aria-label="Review subscription application">
        <header className="pos-modal-head">
          <h3>Review application</h3>
          <button type="button" className="pos-modal-close pos-modal-close-text" onClick={onClose} disabled={busy} aria-label="Close">&times;</button>
        </header>

        <div className="pos-modal-body">
          <dl className="ps-summary">
            <div><dt>Client</dt><dd>{application.clientName} <code className="pa-code">/{application.clientId}</code></dd></div>
            <div><dt>Plan</dt><dd>{application.planName} · {formatPlanDuration(application.durationValue, application.durationUnit)}</dd></div>
            <div><dt>Plan price</dt><dd>{formatMoney(application.planPrice, config)}</dd></div>
            <div>
              <dt>Extra staff</dt>
              <dd>
                {extras.length === 0 ? 'None' : (
                  <>
                    {freeStaffCount > 0 && `${freeStaffCount} included with the plan · `}
                    {paidStaffCount} × {formatMoney(application.extraStaffPrice, config)} = {formatMoney(application.extraStaffTotal, config)}
                    <ul className="ps-email-list">{extras.map((email) => <li key={email}>{email}</li>)}</ul>
                  </>
                )}
              </dd>
            </div>
            <div className="ps-summary-total"><dt>Total paid</dt><dd>{formatMoney(application.total, config)}</dd></div>
            <div><dt>Submitted</dt><dd>{new Date(application.submittedAt).toLocaleString()} by {application.submittedBy}</dd></div>
            <div>
              <dt>If approved</dt>
              <dd>
                {dates
                  ? `${formatPlanDate(dates.startDate)} – ${formatPlanDate(dates.endDate)} (${formatTimeZoneLabel(dates.timeZone)})${extendsCurrent ? ' — starts the day after their current period ends' : ''}`
                  : 'Working out the dates…'}
              </dd>
            </div>
          </dl>

          <div className="ps-receipt">
            <span className="pa-field-label">Proof of payment</span>
            {receiptLoading ? (
              <div className="pa-loading pa-loading-inline">{spinnerRing('Loading receipt')}</div>
            ) : receipt ? (
              <a href={receipt} target="_blank" rel="noreferrer" className="ps-receipt-link">
                <img src={receipt} alt="Payment receipt" />
              </a>
            ) : (
              <p className="pa-field-hint">No receipt image was found for this application.</p>
            )}
          </div>

          {rejecting && (
            <label className="pa-field">
              <span className="pa-field-label">Reason for rejecting</span>
              <textarea
                className="pa-input pa-textarea"
                rows={3}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={POS_APPLICATION_REJECT_REASON_MAX_LENGTH}
                placeholder="The amount on the receipt does not match the total. Please pay the balance and apply again."
                disabled={busy}
              />
              <span className="pa-field-hint">The client sees this on their till.</span>
            </label>
          )}

          {busy && <div className="pa-loading pa-loading-inline">{spinnerRing(rejecting ? 'Rejecting' : 'Activating')}</div>}
          {error && <p className="pa-role-error">{error}</p>}
        </div>

        <footer className="pos-modal-foot">
          {rejecting ? (
            <>
              <button type="button" className="pa-btn pa-btn-quiet" onClick={() => setRejecting(false)} disabled={busy}>Back</button>
              <span className="pos-foot-spacer" />
              <button type="button" className="pa-btn ps-btn-danger" onClick={handleReject} disabled={busy || !reason.trim()}>Reject application</button>
            </>
          ) : (
            <>
              <button type="button" className="pa-btn pa-btn-quiet" onClick={() => setRejecting(true)} disabled={busy}>Reject</button>
              <span className="pos-foot-spacer" />
              <button type="button" className="pa-btn pa-btn-quiet" onClick={onClose} disabled={busy}>Close</button>
              <button type="button" className="pa-btn pa-btn-primary" onClick={handleApprove} disabled={busy || !dates}>Approve &amp; activate</button>
            </>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosApplicationReviewModal;
